import { useCallback } from 'react';
import { useFlagService } from '@atlassian/jira-flags';
import {
	JIRA_SUPPORT_LINK,
	SLACK_CREATE_CONNECTION_EVENT_ID,
	SLACK_USER_LEVEL_AUTH_EVENT_ID,
	SlackErrorCodes,
} from '../../common/constants/index.tsx';
import { chatOpsMessageHandler } from '../../common/utils/connection/index.tsx';
import messages from './messages.tsx';

export const useSlackConnection = ({
	onSuccess,
}: {
	onSuccess: () => Promise<PromiseSettledResult<void>[]>;
}) => {
	const { showFlag } = useFlagService();

	const slackConnectHandler = useCallback(
		(event: MessageEvent) =>
			chatOpsMessageHandler({
				event,
				requiredEventId: SLACK_CREATE_CONNECTION_EVENT_ID,
				onSuccessCallback: onSuccess,
				onFailedCallback: (errorCode: unknown) => {
					switch (errorCode) {
						case SlackErrorCodes.AUTH_CODE_MISSING: {
							showFlag({
								type: 'error',
								title: messages.slackNotConnectedTitleNonFinal,
								description: messages.slackAuthoriseErrorDescriptionNonFinal,
							});
							break;
						}
						case SlackErrorCodes.CONNECTION_ALREADY_EXIST: {
							showFlag({
								type: 'error',
								title: messages.slackNotConnectedTitleNonFinal,
								description: messages.connectionAlreadyExistsDescriptionNonFinal,
							});
							break;
						}
						case SlackErrorCodes.CONNECTION_CREATION_FAILED: {
							showFlag({
								type: 'error',
								title: messages.slackNotConnectedTitleNonFinal,
								description: messages.connectionFailedDescriptionNonFinal,
								actions: [
									{
										content: messages.slackSupportBtnLabelNonFinal,
										href: JIRA_SUPPORT_LINK,
										target: '_blank',
									},
								],
							});
							break;
						}
						case SlackErrorCodes.FETCH_TOKEN_FAILED: {
							showFlag({
								type: 'error',
								title: messages.slackNotConnectedTitleNonFinal,
								description: messages.slackAuthoriseErrorDescriptionNonFinal,
							});
							break;
						}
						case SlackErrorCodes.FORBIDDEN_TENANT: {
							showFlag({
								type: 'error',
								title: messages.somethingWentWrongTitleNonFinal,
								description: messages.slackContactSupportDescriptionNonFinal,
								actions: [
									{
										content: messages.slackSupportBtnLabelNonFinal,
										href: JIRA_SUPPORT_LINK,
										target: '_blank',
									},
								],
							});
							break;
						}
						case SlackErrorCodes.INVALID_STATE: {
							showFlag({
								type: 'error',
								title: messages.slackNotConnectedTitleNonFinal,
								description: messages.slackConnectAgainDescriptionNonFinal,
							});
							break;
						}
						case SlackErrorCodes.LINK_EXPIRED: {
							showFlag({
								type: 'error',
								title: messages.slackSessionExpiredTitleNonFinal,
								description: messages.slackConnectAgainDescriptionNonFinal,
							});
							break;
						}
						case SlackErrorCodes.STATE_DECRYPTION_FAILED: {
							showFlag({
								type: 'error',
								title: messages.slackNotConnectedTitleNonFinal,
								description: messages.slackConnectAgainDescriptionNonFinal,
							});
							break;
						}
						case SlackErrorCodes.WORKSPACE_MISSMATCH: {
							showFlag({
								type: 'error',
								title: messages.slackWorkspaceMismatchTitleNonFinal,
								description: messages.slackWorkSpaceMissmatchDescriptionNonFinal,
							});
							break;
						}
						default: {
							showFlag({
								type: 'error',
								title: messages.somethingWentWrongTitleNonFinal,
								description: messages.slackContactSupportDescriptionNonFinal,
								actions: [
									{
										content: messages.slackSupportBtnLabelNonFinal,
										href: JIRA_SUPPORT_LINK,
										target: '_blank',
									},
								],
							});
							break;
						}
					}
				},
			}),
		[onSuccess, showFlag],
	);

	const slackUserLevelAuthoriseHandler = useCallback(
		(event: MessageEvent) => {
			chatOpsMessageHandler({
				event,
				requiredEventId: SLACK_USER_LEVEL_AUTH_EVENT_ID,
				onSuccessCallback: () => {
					onSuccess();
				},
			});
		},
		[onSuccess],
	);

	return {
		slackConnectHandler,
		slackUserLevelAuthoriseHandler,
	};
};
